//Imports
import React, {
    Fragment,
    useEffect,
    useRef,
    useState,
    useCallback
} from "react";
import moment from 'moment';
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, workOrdersSearchWorkOrders } from "./redux/slice";
import { sendGenericExport, clearRedux, openModalDeleteWorkOrder, savePreference, openModalCreateEditWorkOrder, openModalChangeStatusWorkOrder, openViewAnswersModal } from "Redux/actions";
import { openModalAdvancedFilters } from './modals/advancedFilters/redux/slice'

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination,
    Pill
} from "@gpstrackit/gtc-react-controls";
import { Grid, Icon } from "@mui/material";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import MapModal from "Modules/.common/components/MapModal";
import Preferences from "Core/containers/Preferences";
import { Timezone } from 'Components/uiControls'
import { Avatar } from 'Components';

//Helpers
import { getInitialRange, completeColumns } from "Core/data/Helpers";
import { GetFormatForMoment, splitTextIntoLines } from 'Core/data/Helpers';

//Views
import AdvancedFilters from './modals/advancedFilters'
import DeleteWorkOrder from '../trackerTab/modals/deleteWorkOrder';
import CreateEditWorkOrder from '../trackerTab/modals/createEditWorkOrder';
import ChangeStatus from '../trackerTab/modals/changeStatus';
import ViewAnswers from '../../../dynamicForms/modals/viewAnswers';

//Styles
import './index.scss';

//Variables
const KEY_PREFERENCE = "WOWorkOrderTab-list";
const INITIAL_STATE = {
    type: null,
    status: null,
    severity: null,
    driver: null,
    rangeDate: {
        start: moment().startOf('month'),
        end: moment().endOf('month')
    },
    timeFrame: 'thisMonth'
}

const WorkOrderTab = ({ customerId, fullHeight }) => {

    //States
    const [selectedItems, setSelectedItems] = useState([]);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [preferences, setPreferences] = useState({});
    const [loadPreference, setLoadPreferences] = useState(false);
    const [loadColumns, setLoadColumns] = useState(false);
    const [dataToMap, setDataToMap] = useState({});
    const [openSearch, setOpenSearch] = useState(null);

    //Table refs
    const workOrderTableRef = useRef();

    //Language
    const { messages, locale } = useIntl();

    let severityList = [
        { id: "Low", name: messages['workOrder_createEdit_low'], color: "#66bb6a" },
        { id: "Medium", name: messages['workOrder_createEdit_medium'], color: "#FFB53E" },
        { id: "High", name: messages['workOrder_createEdit_high'], color: "#df1a31" }
    ]

    //Redux States
    const openModalFilters = useSelector((state) => state.workOrdersWorkOrderTabAdvancedFiltersRedux.open);
    const { loading, workOrders, totalWorkOrders, error } = useSelector((state) => state.workOrdersWorkOrderTabRedux);
    const openModalCreateEdit = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux.open);
    const openModalDelete = useSelector((state) => state.workOrdersDeleteWorkOrderRedux?.open);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const openModalChangeStatus = useSelector((state) => state.workOrdersChangeStatusRedux.open);
    const openModalViewAnswers = useSelector(state => state.dynamicFormsViewAnswersRedux.open);

    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone } = user;

    const format = GetFormatForMoment(dateformat);

    const dispatch = useDispatch();

    const paginationWorkOrders = useTablePagination({
        dispatch,
        action: workOrdersSearchWorkOrders,
        searchFields: ["id", "workOrderNumber"],
        tableRef: workOrderTableRef,
        sendGenericExport,
        exportName: "workOrders",
        termsFields: [{
            fields: "customerId",
            terms: [+customerId],
            exact: true
        }]
    });

    const getWorkOrderMenuOptions = () => [
        messages["edit"],
        messages["delete"],
        messages["workOrder_trackerTab_changeStatus"]
    ];

    const handleClickWorkOrderMenu = (type, workOrder) => {
        if (type === messages["delete"]) {
            dispatch(openModalDeleteWorkOrder({ workOrder: workOrder }))
        }
        if (type === messages["edit"]) {
            dispatch(dispatch(openModalCreateEditWorkOrder({ workOrderId: workOrder?.id })))
        } else if (type === messages["workOrder_trackerTab_changeStatus"]) {
            dispatch(openModalChangeStatusWorkOrder({ workOrder: workOrder }))
        }
    }

    const applyChanges = (field, value) => {
        const updatedParams = {
            [field]: value,
            _conditions: buildConditions(formData),
        };
        if (field === '_pageSize') {
            updatedParams._page = 1;
        }

        if (field === '_searchText') {
            setOpenSearch(value);
            if (window.typingTime) {
                clearTimeout(window.typingTime);
            }
            window.typingTime = setTimeout(() => {
                paginationWorkOrders.refresh(updatedParams);
            }, 1000);
        } else {
            paginationWorkOrders.refresh(updatedParams);
        }
    }

    const hasFilters = useCallback(() => {
        const excludedKeys = ["rangeDate"];
        const defaultValues = {
            type: null,
            status: null,
            severity: null,
            timeFrame: "thisMonth",
            driver: null
        };
        return Object.keys(formData).some(key =>
            !excludedKeys.includes(key) &&
            formData[key] !== defaultValues[key]
        );
    }, [formData]);

    const refreshTable = () => {
        setSelectedItems([]);
        workOrderTableRef?.current?.setPage(1);
        workOrderTableRef?.current?.setSelectedItems([]);
        applyChanges('_page', 1)
    };

    const savePreferences = (preferencesArray) => {
        if (!Array.isArray(preferencesArray)) {
            console.error("Expected an array of preferences");
            return;
        }

        let updatedPreferences = { ...preferences } || {};

        preferencesArray.forEach(({ key, value }) => {
            updatedPreferences[key] = value;
        });

        setPreferences(updatedPreferences);

        dispatch(savePreference({
            entityName: "User",
            entityId: authenticatedId,
            preferenceKey: KEY_PREFERENCE,
            preferenceValue: updatedPreferences
        }));
    };

    //Columns
    let currentColumns = [
        {
            key: "workOrderNumber",
            title: messages['workOrder_workOrderTab_workOrderNumber'],
            functionString: 'return row?.workOrderNumber',
            sort: true,
            sortField: "workOrderNumber",
            show: true,
            export: true,
            width: 170,
            exportValue: (row) => row?.workOrderNumber,
            render: (row) => (
                <Grid container alignItems="center">
                    <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 100 }}>
                            <span>{row?.workOrderNumber}</span>
                        </div>
                    </Grid>
                    <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                        {
                            row?.dynamicFormAnswerId && (
                                <IconButton
                                    tooltip={messages['workOrder_workOrderTab_viewAnswer']}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="feed_icon"
                                    size="small"
                                    onClick={() => {
                                        dispatch(openViewAnswersModal(row?.dynamicFormAnswerId))
                                    }}
                                />
                            )
                        }
                        <MenuComponent
                            placement="right-start"
                            options={getWorkOrderMenuOptions()}
                            icon="more_vert"
                            className="showInHover"
                            onClick={handleClickWorkOrderMenu}
                            value={row}
                        />
                    </Grid>
                </Grid>
            )
        },
        {
            key: "driver_title",
            title: messages['workOrder_customerTab_driver'],
            functionString: `
                function splitTextIntoLines(text, maxLength) {
                    let result = "";
                    for (let i = 0; i < text.length; i += maxLength) {
                        result += text.slice(i, i + maxLength) + "\\n";
                    }
                    return result.trim();
                };
                return row?.driver?.title ? splitTextIntoLines(row?.driver?.title, 30) : "";
            `,
            sort: true,
            sortField: "driver_title",
            show: true,
            export: true,
            width: 250,
            exportValue: (row) => row?.driver?.title ? splitTextIntoLines(row.driver.title, 30) : "",
            render: (row) => {
                let fileKey = row?.driver?.avatarFile?.fileKey || ''
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }} key={fileKey}>
                        <div style={{ width: 50 }}>
                            <Avatar fileKey={fileKey} name={row?.driver?.title} />
                        </div>
                        <div style={{ wordWrap: 'break-word', width: 200 }}>
                            <span style={{ marginLeft: 5 }}>{row?.driver?.title}</span>
                        </div>
                    </div>
                )
            }
        },
        {
            key: "status_name",
            title: messages['workOrder_workOrderTab_status'],
            functionString: 'return row?.status?.name',
            sort: true,
            sortField: "status_name",
            show: true,
            export: true,
            width: 140,
            exportValue: (row) => row?.status?.name,
            render: (row) => (
                <Pill
                    size="dinamic"
                    item={{ id: row?.status?.id, label: row?.status?.name, color: `#${row?.status?.color}` }}
                />
            )
        },
        {
            key: "scheduledDate",
            title: messages['workOrder_workOrderTab_scheduledDate'],
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format(formatDate)
                    return formatted;
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a, z"

                return row?.scheduledDate ? formatDate(row?.scheduledDate,timeZone,format) : ''
            `,
            sort: true,
            sortField: "scheduledDate",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.scheduledDate,
            render: (row) => (
                <Timezone
                    date={row?.scheduledDate}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        },
        {
            key: "statusUpdatedOn",
            title: messages['workOrder_workOrderTab_statusUpdatedOn'],
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format(formatDate)
                    return formatted;
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a, z"

                return row?.statusUpdatedOn ? formatDate(row?.statusUpdatedOn,timeZone,format) : ''
            `,
            sort: true,
            sortField: "statusUpdatedOn",
            show: true,
            export: true,
            width: 200,
            exportValue: (row) => row?.statusUpdatedOn,
            render: (row) => (
                <Timezone
                    date={row?.statusUpdatedOn}
                    dateFormat={dateformat}
                    timeFormat="hh:mm a"
                    timezone={timeZone}
                />
            )
        },
        {
            key: "severity",
            title: messages['workOrder_workOrderTab_severity'],
            functionString: 'return row?.severity',
            sort: true,
            sortField: "severity",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return severity?.name || row?.severity
            },
            render: (row) => {
                let severity = severityList.find((item) => item?.id === row?.severity)
                return (
                    <Pill
                        size="dinamic"
                        item={{ id: row?.id, label: severity?.name, color: `${severity?.color}` }}
                    />
                )
            }
        },
        {
            key: "customerLocation_address",
            title: messages['workOrder_workOrderTab_address'],
            functionString: `
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }

                return currentAddress?.address || ""
            `,
            show: true,
            export: true,
            width: 400,
            exportValue: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }

                return currentAddress?.address || ""
            },
            render: (row) => {
                let currentAddress = ''
                try {
                    currentAddress = JSON.parse(row?.customerLocation?.address)
                } catch (error) { }

                return (
                    <Grid container alignItems="center">
                        {currentAddress?.address && (
                            <Grid
                                className="trackerTab-location-address"
                                item
                                onClick={() => {
                                    const data = {
                                        show: true,
                                        title: currentAddress.address,
                                        isPointerMarker: true,
                                        pointers: [
                                            {
                                                latitude: row.customerLocation?.latitude,
                                                longitude: row.customerLocation?.longitude,
                                                label: "Map Point",
                                                heading: 0,
                                                color: "000",
                                                eventName: "no event",
                                                draggable: false
                                            }
                                        ]
                                    };

                                    setDataToMap(data);
                                }}
                            >
                                <Icon className="iconFlag">room</Icon>
                                <p>{currentAddress.address || ""}</p>
                            </Grid>
                        )}
                    </Grid>
                )
            }
        },
        {
            key: "workOrderType_name",
            title: messages['workOrder_workOrderTab_type'],
            functionString: 'return row?.workOrderType?.name',
            sort: true,
            sortField: "workOrderType_name",
            show: true,
            export: true,
            width: 150,
            exportValue: (row) => row?.workOrderType?.name,
            render: (row) => <span>{row?.workOrderType?.name}</span>
        }
    ]

    useEffect(() => {
        paginationWorkOrders.setColumns(currentColumns);
        setSelectedItems([]);
        workOrderTableRef?.current?.setPage(1);
        workOrderTableRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        if (error) {
            NotificationManager.error(messages["errorMessage"], messages["error"], 3000);
            dispatch(reset({ error: null }));
        }
        if (exportResult && exportName === "workOrders") {
            NotificationManager.info(messages["exportInProcessBody"], messages["exportInProcessTitle"], 4000);
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [error, exportResult, exportName]);

    useEffect(() => {
        return () => {
            dispatch(reset())
        };
    }, [])

    useEffect(() => {
        if (preferences?.columns?.length && !loadColumns) {
            const preferenceMap = new Map(preferences.columns.map(col => [col.key, col]));

            // Reorder columns based on preferences (keeping `show`)
            const reorderedColumns = preferences.columns
                .map(pref => {
                    const col = currentColumns.find(c => c.key === pref.key);
                    return col ? { ...col, show: pref.show } : null;
                })
                .filter(Boolean);

            // Add new columns (not listed in preferences), with `show: true` by default
            const newColumns = currentColumns
                .filter(col => !preferenceMap.has(col.key))
                .map(col => ({ ...col, show: true }));

            const finalColumns = [...reorderedColumns, ...newColumns];

            paginationWorkOrders.setColumns(finalColumns);
            setPreferences({ ...preferences });
            setLoadColumns(true);
        }
    }, [preferences.columns, loadColumns])

    useEffect(() => {
        if (paginationWorkOrders.columns.length > 0) {
            setPreferences({ ...preferences, columns: paginationWorkOrders.columns })
            setLoadColumns(false);
        }
    }, [locale, timeZone, dateformat]);

    return (
        <Fragment>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            <IconButton
                                tooltip={messages["filters"]}
                                placement="bottom"
                                icon="filter_list"
                                size="small"
                                disabled={true || loading}
                                onClick={() => dispatch(openModalAdvancedFilters({ open: !openModalFilters }))}
                                className={hasFilters() ? "workOrdersTab-filters-available" : ""}
                            />
                            {
                                selectedItems?.length ? (
                                    <IconButton
                                        tooltip={messages["delete"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="delete"
                                        size="small"
                                        onClick={() => {
                                            dispatch(openModalDeleteWorkOrder({ workOrderList: selectedItems }))
                                        }}
                                    />
                                ) : <></>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationWorkOrders}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    paginationWorkOrders.onExport(type);
                                }}
                                onChangeColumns={(columns) => {
                                    savePreferences([{ key: "columns", value: columns }])
                                    paginationWorkOrders.setColumns(columns)
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["search"]}
                        searchText={openSearch}
                        onSearchText={(value) => applyChanges('_searchText', value)}
                    />
                </Grid>
            </Grid>
            <Preferences
                preferencesKey={KEY_PREFERENCE}
                onInitialPreferences={(data) => {
                    if (!loadPreference) {
                        let updatedFormData = {
                            ...formData, ...['severity', 'status', 'type', 'rangeDate', 'timeFrame', 'driver'].reduce((acc, key) => {
                                if (data?.[key]) acc[key] = data[key];
                                return acc;
                            }, {})
                        };
                        if (data?.timeFrame) {
                            const timeFrame = getInitialRange(data.timeFrame, data?.rangeDate?.start, data?.rangeDate?.end);
                            if (timeFrame?.start && timeFrame?.end) {
                                updatedFormData = {
                                    ...updatedFormData,
                                    rangeDate: timeFrame,
                                    timeFrame: data.timeFrame
                                }
                            }
                        }
                        const currentConditions = buildConditions(updatedFormData);
                        paginationWorkOrders.refresh({ _conditions: currentConditions });
                        setFormData(updatedFormData)
                        setPreferences(data)
                        setLoadPreferences(true)
                    }
                }}
            >
                <TableView
                    {...paginationWorkOrders}
                    keyColumn="id"
                    loading={loading}
                    topheight={fullHeight ? 280 : 340}
                    data={workOrders || []}
                    total={totalWorkOrders}
                    exportOrientation="landscape"
                    onSelectedItems={(items) => setSelectedItems(items)}
                    onChangeSort={(value) => applyChanges('_sort', value)}
                    onChangeRowsPerPage={(value) => applyChanges('_pageSize', value)}
                    onChangePage={(value) => applyChanges('_page', value)}
                    onChangeColumns={(columns) => {
                        let currentColumns = paginationWorkOrders?.columns || []
                        let finalsColumns = completeColumns(currentColumns, columns)
                        savePreferences([{ key: "columns", value: finalsColumns }])
                        paginationWorkOrders.setColumns(finalsColumns)
                    }}
                    hideAllInPagination
                />
                <AdvancedFilters
                    tableRef={paginationWorkOrders}
                    formData={formData}
                    setFormData={setFormData}
                    initialState={INITIAL_STATE}
                    applyChanges={applyChanges}
                    hasFilters={hasFilters}
                    savePreferences={savePreferences}
                />
            </Preferences>
            {openModalDelete && <DeleteWorkOrder refresh={refreshTable} />}
            {openModalCreateEdit && <CreateEditWorkOrder refresh={refreshTable} customerId={customerId} />}
            {openModalChangeStatus && <ChangeStatus refresh={refreshTable} />}
            {openModalViewAnswers && <ViewAnswers />}
            {
                dataToMap?.show &&
                <MapModal
                    hiddenButton
                    {...dataToMap}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenSearchMap={true}
                    hideModal={() => {
                        setDataToMap({ show: false });
                    }}
                    zoom={18}
                />
            }
        </Fragment>
    );
};

const buildConditions = (formData) => {
    const conditions = [];
    if (formData?.status?.id) conditions.push({ fields: "statusId", terms: [formData.status.id], exact: true });
    if (formData?.severity?.id) conditions.push({ fields: "severity", terms: [formData.severity.id], exact: true });
    if (formData?.type?.id) conditions.push({ fields: "workOrderTypeId", terms: [formData.type.id], exact: true });
    if (formData?.driver?.driverId) conditions.push({ fields: "driverId", terms: [formData.driver.driverId], exact: true });
    conditions.push({ range: { fields: ["scheduledDate"], GTE: formData.rangeDate.start, LTE: formData.rangeDate.end } });
    return conditions;
};

export default WorkOrderTab;
